import React from 'react';

/**
 * Statische Orientation-Map für unsere 20 branded Editorial-Bilder.
 * So kann `split` automatisch Portrait vs. Landscape rendern.
 */
const IMAGE_ORIENTATION = {
  '/warnleuchte-an.webp': 'portrait',
  '/werkstattrechnung-zu-hoch.webp': 'landscape',
  '/zu-viele-finanzierungen.webp': 'portrait',
  '/fahrzeug-wirklich-wert.webp': 'landscape',
  '/versicherungskosten-multiplizieren.webp': 'landscape',
  '/teuerste-ueberraschung.webp': 'landscape',
  '/tuev-flotte-kostenblock.webp': 'landscape',
  '/panne-kostet-mehr.webp': 'portrait',
  '/kfz-steuer-jedes-jahr.webp': 'landscape',
  '/wartung-ist-pflicht.webp': 'portrait',
  '/reifenwechsel-mal.webp': 'landscape',
  '/zustand-entscheidet-mit.webp': 'landscape',
  '/gebrauchtwagen-ohne-check.webp': 'portrait',
  '/kleine-dellen-grosse-flotte.webp': 'landscape',
  '/steinschlag-wird-teuer.webp': 'landscape',
  '/haendlerankauf-kostet-marge.webp': 'portrait',
  '/zu-viele-fahrzeuge.webp': 'portrait',
  '/flottenversicherung-teuer.webp': 'landscape',
  '/hotelkosten-dienstreisen.webp': 'landscape',
  '/benefits-nach-feierabend.webp': 'portrait',
};

/**
 * EditorialBanner — Foto-Banner mit bereits eingebrannter Headline+Sub
 * (siehe /app/frontend/public/warnleuchte-an.webp u. a.)
 *
 * Vier Layout-Varianten für Rhythmus quer über die Plattform:
 *
 *  variant="editorial" (Standard)
 *    - max-w-6xl, rounded-[2rem], sanfter Schadow → ruhig, harmonisch
 *
 *  variant="fullbleed"
 *    - kant-zu-kant, kein Rounding, kein Padding-x
 *    - für Landing-Impact und Business-Hero
 *
 *  variant="split"
 *    - 12-col Grid: Bild links (col-span-8) + dunkle Info-/CTA-Card rechts (col-span-4)
 *    - „daneben ein Text" wie vom Nutzer gewünscht
 *
 *  variant="poster"
 *    - max-w-3xl, mittiger schmalerer Container, mit Eyebrow oberhalb + Caption unten
 *    - Für Portrait-Bilder oder ruhige USP-Detail-Seiten
 *
 * Optional:
 *  - eyebrow: Kapitel-Marker (klein, uppercase) über dem Bild
 *  - caption: kleine, kursive Bildunterschrift unter dem Bild
 *  - side: JSX-Content, den `split`-Variante rechts neben dem Bild rendert
 *  - bg: Sektion-Background (default 'bg-white')
 */
export default function EditorialBanner({
  src,
  alt,
  variant = 'editorial',
  orientation, // 'portrait' | 'landscape' — nur für split-Variante relevant
  eyebrow,
  caption,
  side,
  bg = 'bg-white',
  testId,
}) {
  if (variant === 'fullbleed') {
    return (
      <section className="bg-white" data-testid={testId}>
        <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
      </section>
    );
  }

  if (variant === 'poster') {
    return (
      <section className={`${bg} py-14 sm:py-20`} data-testid={testId}>
        <div className="max-w-3xl mx-auto px-6">
          {eyebrow && (
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-700 mb-5 pl-1">
              {eyebrow}
            </p>
          )}
          <div className="rounded-[1.5rem] overflow-hidden bg-stone-50 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.22)]">
            <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
          </div>
          {caption && (
            <p className="mt-4 text-[13px] text-stone-500 pl-1 italic max-w-lg">{caption}</p>
          )}
        </div>
      </section>
    );
  }

  if (variant === 'split') {
    // Portrait vs Landscape — auto-detect via Map, prop überschreibt
    const resolvedOrientation = orientation || IMAGE_ORIENTATION[src] || 'landscape';
    const isPortrait = resolvedOrientation === 'portrait';
    const gridCols = isPortrait
      ? 'lg:grid-cols-[minmax(0,440px)_1fr]'      // Bild schmaler, Text-Card breiter
      : 'lg:grid-cols-12';                        // Landscape: 8/4-Split
    const imgColClass = isPortrait
      ? 'rounded-[2rem] overflow-hidden bg-stone-100 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.18)]'
      : 'lg:col-span-8 rounded-[2rem] overflow-hidden bg-stone-100 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.18)] flex items-center justify-center';
    const sideColClass = isPortrait
      ? 'flex'
      : 'lg:col-span-4 flex';
    return (
      <section className={`${bg} py-14 sm:py-20`} data-testid={testId}>
        <div className={`max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 ${gridCols} gap-5 lg:gap-6 items-stretch`}>
          <div className={imgColClass}>
            <img
              src={src}
              alt={alt}
              className={isPortrait
                ? 'w-full h-full object-cover block'
                : 'w-full h-auto object-contain block max-h-[560px]'
              }
              loading="lazy"
            />
          </div>
          <div className={sideColClass}>
            {side || (
              <div className="w-full bg-stone-950 text-white rounded-[2rem] p-8" />
            )}
          </div>
        </div>
      </section>
    );
  }

  // editorial (default)
  return (
    <section className={`${bg} py-10 sm:py-14`} data-testid={testId}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {eyebrow && (
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-700 mb-4">
            {eyebrow}
          </p>
        )}
        <div className="relative rounded-[2rem] overflow-hidden bg-stone-50 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.18)]">
          <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
        </div>
        {caption && (
          <p className="mt-4 text-[13px] text-stone-500 max-w-2xl italic">{caption}</p>
        )}
      </div>
    </section>
  );
}
